// src/rms/pages/Dispatch.js
// ============================================================================
// Dispatch — delivery-enabled verticals (construction, party equipment)
// ============================================================================
// Construction equipment doesn't get driven off the lot; it gets floated to a
// job site on a trailer. That makes delivery and pickup real scheduled legs
// with their own address, contact, transport class and permit requirement —
// not a note on the booking.
// ============================================================================

import React, { useMemo, useState } from "react";
import { useRms } from "../context/RmsContext";
import { optionsForField } from "../verticals/presets";
import { optionLabel } from "../i18n/optionLabel";
import { useCollection, indexById } from "../hooks/useCollection";
import db from "../data";
import rentalService from "../data/rentalService";
import { PageHeader, Stat, StatusPill, Modal, Field, TextInput, Select, Banner, EmptyState, formatDateTime } from "../components/ui";
import adEngine from "../ads/adEngine";
import { noonInputInDays } from "../utils/datetime";

// Transport classes come from the vertical's own transport_class field rather
// than a second list kept here, which is how this one drifted from the preset.

function Dispatch() {
  const { operator, config, t, formatMoney, invalidate } = useRms();
  const transportClasses = useMemo(
    () => optionsForField(config, "transport_class"),
    [config]
  );
  const { rows: deliveries } = useCollection("deliveries", {}, { orderBy: "scheduled_at" });
  const { rows: assets } = useCollection("assets");
  const { rows: reservations } = useCollection("reservations");

  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);

  const assetsById = useMemo(() => indexById(assets), [assets]);

  const scheduled = deliveries.filter((d) => ["scheduled", "dispatched", "in_transit"].includes(d.status));
  const permitJobs = scheduled.filter((d) => d.permit_required);

  const handleCreate = async (draft) => {
    adEngine.recordAction();
    const result = await rentalService.scheduleDelivery(operator, draft);
    if (!result.success) {
      setError(result.error);
      return;
    }
    setError(null);
    setCreating(false);
    invalidate();
  };

  const advance = async (delivery, status) => {
    adEngine.recordAction();
    await db.update("deliveries", delivery.id, {
      status,
      completed_at: status === "completed" ? new Date().toISOString() : null,
    });
    invalidate();
  };

  return (
    <>
      <PageHeader
        title={t("dispatch.dispatch", "Dispatch")}
        subtitle={t("dispatch.subtitle", "{count, plural, one {# leg scheduled} other {# legs scheduled}}", {
          count: scheduled.length,
        })}
        actions={
          <button type="button" className="rms-btn rms-btn--primary" onClick={() => setCreating(true)}>
            {t("dispatch.scheduleALeg", "Schedule a leg")}
          </button>
        }
      />

      {error ? <Banner tone="danger">{error}</Banner> : null}

      {permitJobs.length ? (
        <Banner tone="warn">
          <strong>
            {t("dispatch.permitWarning", "{count, plural, one {# load needs an oversize permit.} other {# loads need an oversize permit.}}", {
              count: permitJobs.length,
            })}
          </strong>{" "}
          {t("dispatch.permitBody", "Confirm the permit is in hand before the truck rolls.")}
        </Banner>
      ) : null}

      <div className="rms-stat-row">
        <Stat label={t("dispatch.scheduled", "Scheduled")} value={deliveries.filter((d) => d.status === "scheduled").length} />
        <Stat label={t("dispatch.inTransit", "In transit")} value={deliveries.filter((d) => ["dispatched", "in_transit"].includes(d.status)).length} />
        <Stat label={t("dispatch.completed", "Completed")} value={deliveries.filter((d) => d.status === "completed").length} />
        <Stat label={t("dispatch.deliveryRevenue", "Delivery revenue")} value={formatMoney(deliveries.reduce((s, d) => s + (d.fee_cents || 0), 0))} />
      </div>

      {deliveries.length === 0 ? (
        <EmptyState
          title={t("dispatch.nothingScheduled", "Nothing scheduled")}
          message={t("dispatch.scheduleADeliveryOr", "Schedule a delivery or pickup leg to get equipment to and from site.")}
          action={
            <button type="button" className="rms-btn rms-btn--primary" onClick={() => setCreating(true)}>
              {t("dispatch.scheduleALeg", "Schedule a leg")}
            </button>
          }
        />
      ) : (
        <div className="rms-table-wrap">
          <table className="rms-table">
            <thead>
              <tr>
                <th>{t("accessLog.when", "When")}</th>
                <th>{t("dispatch.leg", "Leg")}</th>
                <th>{t("dispatch.machine", "Machine")}</th>
                <th>{t("dispatch.site", "Site")}</th>
                <th>{t("dispatch.transport", "Transport")}</th>
                <th className="rms-num">{t("dispatch.fee", "Fee")}</th>
                <th>{t("campaignForm.status", "Status")}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {deliveries.map((d) => (
                <tr key={d.id}>
                  <td style={{ fontSize: 12.5 }}>
                    {formatDateTime(d.scheduled_at)}
                    {d.window_start && d.window_end ? (
                      <div style={{ color: "var(--rms-muted)" }}>
                        window {new Date(d.window_start).getHours()}:00–{new Date(d.window_end).getHours()}:00
                      </div>
                    ) : null}
                  </td>
                  <td>
                    <StatusPill status={d.direction === "delivery" ? "active" : "completed"} label={d.direction} />
                  </td>
                  <td>
                    <strong>{assetsById[d.asset_id]?.name || "—"}</strong>
                  </td>
                  <td style={{ fontSize: 12.5, maxWidth: 220 }}>
                    <strong>{d.site_name || d.address_line1}</strong>
                    <div style={{ color: "var(--rms-muted)" }}>
                      {[d.address_line1, d.city].filter(Boolean).join(", ")}
                    </div>
                    {d.site_contact_name ? (
                      <div style={{ color: "var(--rms-muted)" }}>{d.site_contact_name}</div>
                    ) : null}
                  </td>
                  <td style={{ fontSize: 12.5 }}>
                    {optionLabel(transportClasses, d.transport_class) || "—"}
                    {d.permit_required ? (
                      <div style={{ color: "var(--rms-warn)", fontWeight: 620 }}>permit required</div>
                    ) : null}
                  </td>
                  <td className="rms-num">{formatMoney(d.fee_cents)}</td>
                  <td>
                    <StatusPill status={d.status === "completed" ? "completed" : d.status === "scheduled" ? "confirmed" : "active"} label={d.status.replace(/_/g, " ")} />
                  </td>
                  <td className="rms-num" style={{ whiteSpace: "nowrap" }}>
                    {d.status === "scheduled" ? (
                      <button type="button" className="rms-btn rms-btn--ghost rms-btn--sm" onClick={() => advance(d, "in_transit")}>
                        {t("nav.dispatch", "Dispatch")}
                      </button>
                    ) : null}
                    {["dispatched", "in_transit"].includes(d.status) ? (
                      <button type="button" className="rms-btn rms-btn--primary rms-btn--sm" onClick={() => advance(d, "completed")}>
                        {t("dispatch.markDone", "Mark done")}
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {creating ? (
        <DeliveryForm
          assets={assets}
          reservations={reservations}
          currency={operator.currency}
          transportClasses={transportClasses}
          onCancel={() => setCreating(false)}
          onSave={handleCreate}
        />
      ) : null}
    </>
  );
}

function DeliveryForm({ assets, reservations, currency, transportClasses, onCancel, onSave }) {
  const { t } = useRms();
  const [draft, setDraft] = useState({
    asset_id: "",
    reservation_id: "",
    direction: "delivery",
    scheduled_at: noonInputInDays(1),
    site_name: "",
    address_line1: "",
    city: "",
    site_contact_name: "",
    site_contact_phone: "",
    site_notes: "",
    transport_class: "",
    permit_required: false,
    fee: "",
  });
  const [error, setError] = useState(null);
  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  const asset = assets.find((a) => a.id === draft.asset_id);

  // Heavy machines need a permit; suggest it from the asset itself rather than
  // relying on the dispatcher to remember.
  const suggestPermit = (asset?.custom_fields?.operating_weight_kg || 0) > 6000;

  const submit = () => {
    if (!draft.asset_id) return setError("Choose a machine.");
    if (!draft.address_line1.trim()) return setError("A site address is required.");
    setError(null);
    onSave({
      ...draft,
      scheduled_at: new Date(draft.scheduled_at).toISOString(),
      reservation_id: draft.reservation_id || null,
      fee_cents: Math.round(Number(draft.fee || 0) * 100),
      transport_class: draft.transport_class || asset?.custom_fields?.transport_class || null,
      permit_required: draft.permit_required || suggestPermit,
    });
  };

  return (
    <Modal
      title={t("dispatch.scheduleADeliveryLeg", "Schedule a delivery leg")}
      onClose={onCancel}
      footer={
        <>
          <button type="button" className="rms-btn rms-btn--ghost" onClick={onCancel}>
            {t("action.cancel", "Cancel")}
          </button>
          <button type="button" className="rms-btn rms-btn--primary" onClick={submit}>
            {t("pricing.schedule", "Schedule")}
          </button>
        </>
      }
    >
      {error ? <Banner tone="danger">{error}</Banner> : null}

      <div className="rms-field-grid">
        <Field label={t("dispatch.machine", "Machine")} required>
          <Select
            value={draft.asset_id}
            onChange={(v) => set("asset_id", v)}
            placeholder="Choose a machine…"
            options={assets.map((a) => ({ value: a.id, label: `${a.asset_code} — ${a.name}` }))}
          />
        </Field>
        <Field label={t("dispatch.leg", "Leg")}>
          <Select
            value={draft.direction}
            onChange={(v) => set("direction", v)}
            placeholder={t("dispatch.delivery", "Delivery")}
            options={[
              { value: "delivery", label: "Delivery to site", labelKey: "opt.legDirection.delivery" },
              { value: "pickup", label: "Pickup from site", labelKey: "opt.legDirection.pickup" },
            ]}
          />
        </Field>
        <Field label={t("dispatch.scheduled", "Scheduled")} required>
          <input className="rms-input" type="datetime-local" value={draft.scheduled_at} onChange={(e) => set("scheduled_at", e.target.value)} />
        </Field>
        <Field label={t("dispatch.linkedBooking", "Linked booking")}>
          <Select
            value={draft.reservation_id}
            onChange={(v) => set("reservation_id", v)}
            placeholder={t("dispatch.none", "None")}
            options={reservations
              .filter((r) => ["confirmed", "checked_out"].includes(r.status))
              .map((r) => ({ value: r.id, label: r.reservation_number }))}
          />
        </Field>
      </div>

      <div style={{ borderTop: "1px solid var(--rms-border)", paddingTop: 14, marginTop: 4 }}>
        <h3 className="rms-section-title">{t("dispatch.jobSite", "Job site")}</h3>
        <div className="rms-field-grid">
          <Field label={t("dispatch.siteName", "Site name")}>
            <TextInput value={draft.site_name} onChange={(v) => set("site_name", v)} />
          </Field>
          <Field label={t("dispatch.address", "Address")} required>
            <TextInput value={draft.address_line1} onChange={(v) => set("address_line1", v)} />
          </Field>
          <Field label={t("dispatch.city", "City")}>
            <TextInput value={draft.city} onChange={(v) => set("city", v)} />
          </Field>
          <Field label={t("dispatch.siteContact", "Site contact")}>
            <TextInput value={draft.site_contact_name} onChange={(v) => set("site_contact_name", v)} />
          </Field>
          <Field label={t("dispatch.contactPhone", "Contact phone")}>
            <TextInput value={draft.site_contact_phone} onChange={(v) => set("site_contact_phone", v)} />
          </Field>
          <Field label={`Delivery fee (${currency})`}>
            <input className="rms-input" type="number" min="0" step="0.01" value={draft.fee} onChange={(e) => set("fee", e.target.value)} />
          </Field>
        </div>
        <Field label={t("dispatch.accessNotes", "Access notes")} help={t("dispatch.gateCodesOverheadLines", "Gate codes, overhead lines, ground conditions.")}>
          <textarea className="rms-textarea" value={draft.site_notes} onChange={(e) => set("site_notes", e.target.value)} />
        </Field>
      </div>

      <div style={{ borderTop: "1px solid var(--rms-border)", paddingTop: 14 }}>
        <h3 className="rms-section-title">{t("dispatch.transport", "Transport")}</h3>
        <Field
          label={t("dispatch.transportClass", "Transport class")}
          help={
            asset?.custom_fields?.transport_class
              ? `This machine is normally moved on a ${optionLabel(
                  transportClasses,
                  asset.custom_fields.transport_class
                ).toLowerCase()}.`
              : undefined
          }
        >
          <Select
            value={draft.transport_class}
            onChange={(v) => set("transport_class", v)}
            placeholder={
              optionLabel(transportClasses, asset?.custom_fields?.transport_class) || "Choose…"
            }
            options={transportClasses}
          />
        </Field>
        <label className="rms-checkline">
          <input type="checkbox" checked={draft.permit_required || suggestPermit} onChange={(e) => set("permit_required", e.target.checked)} />
          {t("dispatch.oversizePermitRequired", "Oversize permit required")}
        </label>
        {suggestPermit && !draft.permit_required ? (
          <Banner tone="warn">
            {t("dispatch.thisMachineSOperating", "This machine's operating weight is over 6,000 kg — a permit is usually required.")}
          </Banner>
        ) : null}
      </div>
    </Modal>
  );
}


export default Dispatch;
